<template>
  <div
    v-if="!isChromeView"
    class="sidebar-container"
    :class="{ wide: isSideNavOpen }"
  >
    <nav>
      <section class="main-section">
        <ul class="main-list">
          <li
            v-for="(item, itemIdx) in allNavItems"
            :key="itemIdx"
            class="main-category-item"
          >
            <div class="category-icon">
              <v-icon
                :icon="item.icon"
                :class="item.iconClass || ''"
                :size="isSideNavOpen ? 16 : 20"
              />
              <span v-if="!isSideNavOpen" class="hover-runway" />
            </div>
            <div class="menu">
              <span class="category-text">{{ item.name }}</span>
              <ul v-if="item.sections" class="section-list">
                <AppSideNavSections
                  v-for="(section, sectionIdx) in item.sections"
                  :key="sectionIdx"
                  :section="section"
                  :is-open="openedSections[section.id]"
                  :is-side-nav-open="isSideNavOpen"
                  @section-toggle="handleSectionToggle"
                  @open-modal="openModal"
                  @tracking-event="handleTrackingEvent($event)"
                />
              </ul>
            </div>
          </li>
        </ul>
      </section>
    </nav>
    <CreateDraftModal
      :show="show.createDraft"
      :template-list="templateList"
      @close="setModal({ field: 'createDraft', value: false })"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useAppSideNav } from '@/stores/app/appSideNav';
import { useAppPlatform } from '@/stores/app/platform';
import { useEditor } from '@/stores/editor/editor';
import { useTracking } from '@/stores/tracking/tracking';
import { useAppRoute } from '@/stores/app/route';
import { useModals } from '@/stores/modals/modals';
import AppSideNavSections from '@/components/shared/sideNav/AppSideNavSections.vue';
import CreateDraftModal from '@/components/shared/CreateDraftModal.vue';

const route = useRoute();

const { isSideNavOpen } = storeToRefs(useAppSideNav());
const { allNavItems } = storeToRefs(useAppPlatform());

const { initAppSideNavFromLocalStorage } = useAppSideNav();

const { templateList } = storeToRefs(useEditor());

const { isChromeView } = storeToRefs(useAppRoute());

const { show } = storeToRefs(useModals());
const { setModal } = useModals();

const openedSections = ref({});

const openModal = () => {
  setModal({ field: 'createDraft', value: true });
};

const handleSectionToggle = (sectionId) => {
  openedSections.value[sectionId] = !openedSections.value[sectionId];
};

const toggleAllSections = (bool) => {
  const openedSectionsKeys = Object.keys(openedSections.value);
  openedSections.value = openedSectionsKeys.reduce((openSections, curr) => {
    // eslint-disable-next-line no-param-reassign
    openSections[curr] = bool;
    return openSections;
  }, {});
};

const toggleSectionsForCurrentCategory = () => {
  const categoryIds = allNavItems.value.map((item) => item.id);
  const linksByCategory = categoryIds.reduce((categories, curr) => {
    // eslint-disable-next-line no-param-reassign
    categories[curr] = [];
    return categories;
  }, {});

  allNavItems.value.forEach((category) => category.sections.forEach((section) => {
    if (section.links) {
      section.links.forEach((link) => linksByCategory[category.id].push(
        { link: link.to, sectionId: section.id },
      ));
    }
    if (section.to) linksByCategory[category.id].push({ link: section.to, sectionId: section?.id || null });
  }));

  const activeCategory = categoryIds.find((category) => linksByCategory[category]
    .find((link) => link?.link === route?.name));

  toggleAllSections(false); // Close all sections

  if (!activeCategory) return; // If we're on a settings or hidden page, exit

  linksByCategory[activeCategory].forEach((link) => { // Open only current category
    if (link.sectionId) openedSections.value[link.sectionId] = true;
  });
};

const handleTrackingEvent = ({ action, args }) => {
  useTracking()[action](args);
};

onMounted(() => {
  // Initialize open section ref
  allNavItems.value.forEach((category) => category.sections.forEach((section) => {
    if (section.links) openedSections.value[section.id] = false;
  }));

  initAppSideNavFromLocalStorage();

  if (!isSideNavOpen.value) toggleAllSections(true); // Open all when sidenav is closed
  else toggleSectionsForCurrentCategory();
});

watch(
  isSideNavOpen,
  (newValue) => {
    if (!newValue) {
      toggleAllSections(true); // Open all
    } else toggleSectionsForCurrentCategory(); // Open the current category
  },
);
</script>

<style lang="less" scoped>
// Individual items, some rules may get overridden
.section-list {
  padding-top: 4px;
}

.category-icon {
  display: inline-flex;
  min-width: 30px;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  justify-content: center;
  align-items: center;

  :deep(.set-stroke-color path) {
    stroke: @icon-default;
  }

  :deep(.set-fill-color path) {
    fill: @icon-default;
  }

  &:hover {
    cursor: pointer;
    background-color: @bg-dark;
  }

  &:has(+ .menu .router-link-exact-active) {
    background-color: @bg-dark;
    border: 1px solid @stroke-default;
  }
}

.category-text {
  text-transform: uppercase;
  color: @text-light;
  font-size: 11px;
  padding: 0px 8px;

  &:hover {
    cursor: default;
  }
}

// Main containers, will override rules if needed
.sidebar-container {
  max-height: 100%;
  background-color: @bg-default;
  border-right: 1px solid @stroke-default;
  width: 45px;
  min-width: 45px;
  transition: all 300ms ease-out;
  box-sizing: border-box;

  nav {
    padding: 12px 0px;
  }

  li, :deep(li) {
    list-style-type: none;
  }

  .main-list {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    transition: all 400ms ease-in-out;
    padding: 0px 12px 0px 6px;
  }

  .main-category-item {
    display: flex;
    position: relative;
    width: 100%;

    .menu {
      position: absolute;
      display: none;
      background-color: @bg-default;
      border-radius: 6px;
      border: 1px solid @stroke-dark;
      min-width: max-content;
      width: 100%;
      padding: 8px;
    }

    &:hover > .menu {
      display: block;
      left: 45px;
      z-index: 10;
      box-shadow: 3px 3px 10px white; // For contrast if needed

      :deep(.dropdown-list.open) {
        height: fit-content;
        visibility: visible;
      }
    }

    &:hover > .category-icon .hover-runway {
      position: absolute;
      background-color: transparent;
      width: 100px;
      height: 54px;
      top: 0;
      z-index: 10;
    }
  }

  &.wide {
    width: 240px;
    transition: all 400ms ease-out;
    overflow-y: hidden;
    padding-bottom: 32px; // Give breathing room to the last item

    nav {
      height: 100%;
      overflow-y: auto;
      overflow-x: hidden;
    }

    .main-section {
      height: 100%;
    }

    .main-list {
      height: 100%;
      align-items: flex-start;
    }

    .main-category-item {
      .category-icon:has(+ .menu .router-link-exact-active) {
        background-color: unset;
        border: none;
      }

      .menu {
        position: relative;
        display: block;
        left: unset;
        border: none;
        padding: 0px;
        box-shadow: unset;
        margin-top: 3px; // align the title with its icon

        :deep(.dropdown-list.open) {
          height: fit-content;
          visibility: visible;
        }
      }

      .category-icon:hover {
        cursor: default;
        background-color: unset;
      }
    }
  }
}
</style>
